import { FilterService } from '@primevue/core/api';
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { CapacityProviderType, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Carrier, CarrierSearchFilter } from '@/model';

import { VirtualScrollerLazyEvent } from 'primevue/virtualscroller';

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 100;

/**
 * Select and MultiSelect always re-filter the options they are given locally,
 * which for a server search means hiding rows the server already matched and
 * leaving fewer rendered rows than loaded ones, which stalls lazy paging. The
 * filter input still has to exist because typing in it is what triggers the
 * search, so instead of removing it we give it a match mode that keeps
 * everything and let the server decide what matches.
 */
export const SERVER_SEARCH_MATCH_MODE = 'serverSearchPassthrough';
FilterService.register(SERVER_SEARCH_MATCH_MODE, () => true);

export interface UseCarrierSearchOptions {
  searchType?: MaybeRefOrGetter<'assigned' | 'all'>;
  hasEmailConfig?: MaybeRefOrGetter<boolean | undefined>;
  /** Restricts the search to one capacity provider type, e.g. brokers only. */
  type?: MaybeRefOrGetter<CapacityProviderType | undefined>;
}

export interface UseCarrierSearchResponse {
  carriers: Ref<Loadable<Carrier[]>>;
  /**
   * Whether the whole result set fits in one page, so a caller offering a
   * select-all would be acting on every match rather than on the rows that
   * happen to be loaded.
   */
  allCarriersFitSinglePage: ComputedRef<boolean>;
  loadNextPage: () => Promise<void>;
  onLazyLoad: (event: VirtualScrollerLazyEvent) => void;
  onSearch: (query: string) => void;
  onHide: () => void;
}

export const useCarrierSearch = (
  opts: UseCarrierSearchOptions = {},
): UseCarrierSearchResponse => {
  const { t } = useI18n();
  const toast = useToast();

  const carriers = ref<Loadable<Carrier[]>>({ loading: false });
  const nextPage = ref(0);
  const hasMore = ref(true);
  const totalElements = ref(0);
  const currentQuery = ref('');
  // Deliberately not a ref: `ref()` proxies the token source, which breaks the
  // identity checks below that decide whether a response is still the current one.
  let cancelRequest: CancelTokenSource | undefined;

  const allCarriersFitSinglePage = computed(
    () => totalElements.value > 0 && totalElements.value <= PAGE_SIZE,
  );

  const loadNextPage = async () => {
    if (carriers.value.loading || !hasMore.value) {
      return;
    }

    if (!authorization.hasAuthority('carrier', 'read')) {
      console.warn('User does not have authorization to search for carriers');
      return;
    }

    const request = axios.CancelToken.source();
    cancelRequest = request;

    try {
      carriers.value.loading = true;
      const filter = new CarrierSearchFilter({
        query: currentQuery.value,
        page: nextPage.value,
        pageSize: PAGE_SIZE,
        type: toValue(opts.type),
      });

      const result = await api.carrier.search(filter, {
        allCarriers: toValue(opts.searchType) === 'all',
        hasEmailConfig: toValue(opts.hasEmailConfig),
        cancelToken: request,
      });

      // A newer search has already taken over, so this page belongs to a query
      // the user has moved on from.
      if (cancelRequest !== request) {
        return;
      }

      carriers.value.data = [...(carriers.value.data || []), ...result.content];
      nextPage.value += 1;
      hasMore.value = !result.last;
      totalElements.value = result.totalElements;
    } catch (e) {
      if (axios.isCancel(e) || cancelRequest !== request) {
        return;
      }

      carriers.value.error = e;
      console.error('Error searching carriers', e);
      toast.addError({
        detail: t('errors.loadingParam', { param: t('carriers.carriers', 2) }),
        error: e,
      });
    } finally {
      if (cancelRequest === request) {
        cancelRequest = undefined;
        carriers.value.loading = false;
      }
    }
  };

  const resetSearch = () => {
    cancelRequest?.cancel();
    cancelRequest = undefined;
    carriers.value.loading = false;
    carriers.value.error = undefined;
    carriers.value.data = [];
    nextPage.value = 0;
    hasMore.value = true;
    totalElements.value = 0;
  };

  const onLazyLoad = (event: VirtualScrollerLazyEvent) => {
    if (event.last >= (carriers.value.data?.length || 0)) {
      loadNextPage();
    }
  };

  const onSearch = debounce((query: string) => {
    if (currentQuery.value === query) {
      return;
    }

    currentQuery.value = query;
    resetSearch();
    loadNextPage();
  }, SEARCH_DEBOUNCE_MS);

  // `reset-filter-on-hide` wipes the filter text, so the loaded pages have to go
  // with it, or reopening shows the old query's results under an empty filter.
  const onHide = () => {
    onSearch.cancel();
    currentQuery.value = '';
    resetSearch();
  };

  return {
    carriers,
    allCarriersFitSinglePage,
    loadNextPage,
    onLazyLoad,
    onSearch,
    onHide,
  };
};
